import {EditorState, type Extension, MapMode, RangeSetBuilder, StateEffect, StateField} from '@codemirror/state'
import {Decoration, type DecorationSet, EditorView} from '@codemirror/view'

import {getNewlinePrefix, getNewlineSuffix} from '../../language-tools/source-utils'
import {UploadPlaceholderWidget, type UploadPlaceholderWidgetProps} from '../widgets/upload-placeholder'
import {activeNodesExtension, activeSelectionChanged, getActiveSelection} from './active-nodes'

/** Number of milliseconds to show an error before removing the placeholder. */
const ERROR_TIMEOUT = 3_000

interface UploadPlaceholderSpec {
  id: string
  /** Offset of the placeholder in the document. */
  position: number
  data: UploadPlaceholderWidgetProps
}

interface UploadPlaceholderUpdate {
  id: string
  data: Partial<UploadPlaceholderWidgetProps>
}

type PlaceholderEntry = Omit<UploadPlaceholderSpec, 'id'>

interface PlaceholderFieldState {
  entries: ReadonlyMap<string, PlaceholderEntry>
  decorations: DecorationSet
}

const addUploadPlaceholderEffect = StateEffect.define<UploadPlaceholderSpec>()

const updateUploadPlaceholderEffect = StateEffect.define<UploadPlaceholderUpdate>()

const removeUploadPlaceholderEffect = StateEffect.define<{id: string}>()

/**
 * Get the doc as a string, but insert a placeholder character at all pending file uploads. This ensures that
 * `getNewlinePrefix` and `getNewlineSuffix` insert the correct number of newlines, adding space when a new upload is
 * started immediately adjacent to a pending upload.
 */
function buildDocWithPlaceholderChars(
  view: EditorView,
  caretPosition: number,
): [doc: string, adjustedCaretPosition: number] {
  const widgets = getAllEntries(view.state)
  let doc = view.state.doc.toString()
  let adjustedCaretPosition = caretPosition

  const placeholderChar = 'x'

  for (const {position} of widgets) {
    const before = doc.slice(0, position)
    const after = doc.slice(position)
    doc = before + placeholderChar + after
    if (adjustedCaretPosition > position) adjustedCaretPosition += 1
  }

  return [doc, adjustedCaretPosition]
}

/**
 * Add one or more placeholders in a single transaction. Newlines will be automatically inserted around and between
 * each placeholder. Any error placeholders will automatically remove themselves after a delay, although the newlines
 * will still be inserted and will remain.
 * @param position The offset in the document at which to insert the placeholders.
 */
export function addUploadPlaceholders(
  view: EditorView,
  position: number,
  ...specs: Array<Omit<UploadPlaceholderSpec, 'position'>>
) {
  // Adjust the doc and position to consider any existing pending uploads when inserting newlines
  const [doc, adjustedPositionForNewlineCalcs] = buildDocWithPlaceholderChars(view, position)

  const precedingNewlines = getNewlinePrefix(doc, adjustedPositionForNewlineCalcs)
  const intermediateNewlines = '\n\n'.repeat(Math.max(0, specs.length - 1))
  const succeedingNewlines = getNewlineSuffix(doc, adjustedPositionForNewlineCalcs)

  const addPlaceholderEffects = specs.map((spec, i) =>
    // Adjust position of each item to account for inserted newlines, spacing them out with a blank line between each
    addUploadPlaceholderEffect.of({...spec, position: position + precedingNewlines.length + i * 2}),
  )

  view.dispatch({
    effects: addPlaceholderEffects,
    changes: {from: position, insert: `${precedingNewlines}${intermediateNewlines}${succeedingNewlines}`},
    // Move caret down to line after the last item (succeedingNewlines.length could be 1 or 0 if there already was a
    // blank line present, so we hardcode 2 instead)
    selection: {anchor: position + precedingNewlines.length + intermediateNewlines.length + 2},
    scrollIntoView: true,
  })

  // Remove any error placeholders after a timeout
  const errorCleanupEffects = specs
    .filter(spec => spec.data.error)
    .map(spec => removeUploadPlaceholderEffect.of({id: spec.id}))

  if (errorCleanupEffects.length) {
    setTimeout(() => {
      view.dispatch({
        effects: errorCleanupEffects,
      })
    }, ERROR_TIMEOUT)
  }
}

/** Update a placeholder. If an error message is set, the placeholder will remove itself after a delay. */
export function updateUploadPlaceholder(view: EditorView, update: UploadPlaceholderUpdate) {
  view.dispatch({effects: [updateUploadPlaceholderEffect.of(update)]})

  if (update.data.error) {
    setTimeout(() => view.dispatch({effects: [removeUploadPlaceholderEffect.of({id: update.id})]}), ERROR_TIMEOUT)
  }
}

function isActive(state: EditorState, position: number) {
  const selection = getActiveSelection(state)
  return selection?.ranges.some(range => range.to >= position && range.from <= position) ?? false
}

/** Remove the placeholder from the view and replace with the `insert` text. Has no effect if the placeholder has been deleted. */
export function replaceUploadPlaceholder(view: EditorView, id: string, insert: string) {
  const position = view.state.field(uploadPlaceholderField).entries.get(id)?.position ?? null
  // location will be null if the placeholder has been deleted
  if (position === null) return

  // Note: we intentionally do not scroll into view in this case; the upload may complete outside of the current view
  // and scrolling would be disruptive
  view.dispatch({
    effects: [removeUploadPlaceholderEffect.of({id})],
    changes: {from: position, insert},
  })
}

export function getAllEntries(state: EditorState): UploadPlaceholderSpec[] {
  const field = state.field(uploadPlaceholderField)
  return Array.from(field.entries.entries()).map(([id, entry]) => ({
    id,
    ...entry,
  }))
}

const uploadPlaceholderField = StateField.define<PlaceholderFieldState>({
  create: () => ({entries: new Map(), decorations: Decoration.none}),
  update: (state, tr) => {
    const entries = new Map(state.entries)
    let changed = false

    let skipMapping = false

    for (const effect of tr.effects) {
      if (effect.is(addUploadPlaceholderEffect)) {
        const {id, ...entry} = effect.value
        entries.set(id, entry)
        changed = true
        skipMapping = true // we already handle the mapping for this case when adding the placeholder
      } else if (effect.is(updateUploadPlaceholderEffect) && entries.has(effect.value.id)) {
        const existing = entries.get(effect.value.id)!
        entries.set(effect.value.id, {
          ...existing,
          data: {...existing.data, ...effect.value.data},
        })
        changed = true
      } else if (effect.is(removeUploadPlaceholderEffect)) {
        changed = entries.delete(effect.value.id)
      }
    }

    if (tr.docChanged && entries.size > 0 && !skipMapping) {
      for (const [id, entry] of entries) {
        const newPosition = tr.changes.mapPos(entry.position, 1, MapMode.TrackAfter)

        if (newPosition !== entry.position) {
          // If the location is `null`, the placeholder has been deleted and so we forget about it
          if (newPosition === null) {
            entries.delete(id)
          } else {
            entries.set(id, {
              ...entry,
              position: newPosition,
            })
          }

          changed = true
        }
      }
    }

    if (activeSelectionChanged(tr)) {
      for (const [id, entry] of entries) {
        const newActive = isActive(tr.state, entry.position)

        if (newActive !== entry.data.active) {
          entries.set(id, {
            ...entry,
            data: {...entry.data, active: newActive},
          })
          changed = true
        }
      }
    }

    if (!changed) return state

    // Rebuild decorations from scratch for all entries
    const builder = new RangeSetBuilder<Decoration>()
    const sortedEntries = Array.from(entries).sort(([, a], [, b]) => {
      if (a.position !== b.position) return a.position - b.position
      return 0
    })

    for (const [id, entry] of sortedEntries) {
      const decoration = Decoration.replace({
        widget: new UploadPlaceholderWidget(id, entry.data),
      })
      builder.add(entry.position, entry.position + 1, decoration)
    }
    const decorations = builder.finish()

    return {
      entries,
      decorations,
    }
  },
  provide: field => [activeNodesExtension, EditorView.decorations.from(field, value => value.decorations)],
})

export const uploadPlaceholderExtension: Extension = [uploadPlaceholderField]
